import {isNullOrUndefined} from "../../../common/utils";
import {InputType} from "../inputFields/constants";
import {InputFields} from "../inputFields/userGroupInputFields";
import {heightOfNode} from "../utils/heightOfNode";
import {membershipConditionErrorResponse} from "../utils/validateMembershipCondition";
import {SimpleConditionStringRender} from "../view/simpleConditionStringRender";
import SimpleConditionValueRender from "../view/simpleConditionValueRender";
import ConditionNode from "./ConditionNode";
import "./index.css";
import {MembershipConditionsErrors} from "./MembershipConditionErrors";
import {Operators} from "./Operators";

function returnMembershipConditionErrorCodeForAllowedSimpleOperators(conditionNode) {
  const errorForUndefinedInputFields = conditionNode.returnErrorForUndefinedInputFields();
  if (!isNullOrUndefined(errorForUndefinedInputFields.errorCode)) {
    return errorForUndefinedInputFields;
  }
  return conditionNode.returnErrorForConflictInputFieldsAndOperators();
}

class SimpleCondition extends ConditionNode {
  operandKey = "operands";
  addOperands(operands) {
    this[this.operandKey] = operands;
  }
  isSingleOperand() {
    return true;
  }
  getOperands() {
    return this[this.operandKey];
  }
  render() {
    return <div className="condition">{this.renderCond()}</div>;
  }

  renderAsString() {
    return <div>{this.renderCondAsString()}</div>;
  }

  toJSON(requestFormat = false) {
    let jsonResponse = {
      operator: this.operator,
      operands: this.getOperands(),
    };
    if (requestFormat === false) {
      jsonResponse = {
        ...jsonResponse,
        id: this.key,
        pathFromRoot: this.pathFromRoot,
        isKeyCondition: this.isKeyCondition,
      };
    }
    if (!isNullOrUndefined(this.reason)) {
      jsonResponse = { ...jsonResponse, reason: this.reason };
    }
    return jsonResponse;
  }
  setIsKeyCondition() {
    if (this.operator === Operators.comparisonSPECIAL_EQ) {
      this.isKeyCondition = false;
    } else if (heightOfNode(this) === 0) {
      this.isKeyCondition = true;
    } else if (heightOfNode(this) === 1 && this.parent.operator === Operators.logicalOR) {
      this.isKeyCondition = true;
    }
  }
  returnErrorForUndefinedInputFields() {
    let errorCode, message;
    if (isNullOrUndefined(InputFields[this.getOperands().attributeName])) {
      errorCode = MembershipConditionsErrors.NotRecognizedInputField.errorCode;
      message = MembershipConditionsErrors.NotRecognizedInputField.message;
    }
    return membershipConditionErrorResponse(errorCode, this.key, message);
  }
}

export class ArrayContains extends SimpleCondition {
  operator = Operators.comparisonARRAY_CONTAINS;
  constructor(condition, parentNode) {
    super(condition.id, condition.pathFromRoot, parentNode);
    this.setIsKeyCondition();
    this.addOperands(condition[this.operandKey]);
    this.addLabel();
  }
  addLabel() {
    this.label = InputFields[this.getAttributeName()]?.attributeLabel;
  }
  getAttributeName() {
    return this.getOperands().attributeName;
  }
  setValue(value) {
    this[this.operandKey] = { ...this.getOperands(), arrayValueToBeContained: value };
  }
  getValue() {
    return this[this.operandKey].arrayValueToBeContained;
  }
  renderCond(tableStyle) {
    return (
      <SimpleConditionValueRender
        label={this.label}
        value={this[this.operandKey].arrayValueToBeContained}
      />
    );
  }

  renderCondAsString() {
    return (
      <SimpleConditionStringRender
        label={this.label}
        value={this[this.operandKey].arrayValueToBeContained}
      />
    );
  }

  returnErrorForConflictInputFieldsAndOperators() {
    let errorCode, message;
    if (!InputType[InputFields[this.getOperands().attributeName].inputType].allowedOperators.some((operator)=>operator===this.operator)) {
      errorCode = MembershipConditionsErrors.ArrayContainsMismatchInputField.errorCode;
      message = MembershipConditionsErrors.ArrayContainsMismatchInputField.message;
    }
    return membershipConditionErrorResponse(errorCode, this.key, message);
  }
  returnMembershipConditionErrorCode() {
    return returnMembershipConditionErrorCodeForAllowedSimpleOperators(this);
  }
  static createArrayContainsNode(attributeName, value) {
    const attributeType = InputFields[attributeName].attributeType;
    const operands = {
      attributeName,
      attributeType,
      arrayValueToBeContained: value,
    };
    return new ArrayContains({ operands }, null);
  }
}

export class RegexMatch extends SimpleCondition {
  operator = Operators.comparisonREGEX_MATCH;
  constructor(condition, parentNode) {
    super(condition.id, condition.pathFromRoot, parentNode);
    this.setIsKeyCondition();
    this.addOperands(condition[this.operandKey]);
  }
  returnMembershipConditionErrorCode() {
    return membershipConditionErrorResponse(
      MembershipConditionsErrors.NotSupportedOperators.errorCode,
      this.key,
      MembershipConditionsErrors.NotSupportedOperators.message
    );
  }
}
export class Equality extends SimpleCondition {
  operator = Operators.comparisonEQ;
  constructor(condition, parentNode) {
    super(condition.id, condition.pathFromRoot, parentNode);
    this.setIsKeyCondition();
    this.addOperands(condition[this.operandKey]);
    this.addLabel();
    if (condition.key !== undefined) {
      this.key = condition.key
    }
  }
  addLabel() {
    this.label = InputFields[this.getAttributeName()]?.attributeLabel;
  }
  getAttributeName() {
    return this.getOperands().attributeName;
  }
  setValue(value) {
    this[this.operandKey] = { ...this.getOperands(), attributeValueToBeMatched: value };
  }
  getValue() {
    return this[this.operandKey].attributeValueToBeMatched;
  }
  renderCond() {
    return (
      <SimpleConditionValueRender
        label={this.label}
        value={this[this.operandKey].attributeValueToBeMatched}
      />
    );
  }
  renderCondAsString() {
    return (
      <SimpleConditionStringRender
        label={this.label}
        value={this[this.operandKey].attributeValueToBeMatched}
      />
    );
  }

  toStringIn() {
    return StringIn.createStringInNode(this.getAttributeName(),[this.getValue()])
  }

  returnErrorForConflictInputFieldsAndOperators() {
    let errorCode, message;
    if (!InputType[InputFields[this.getOperands().attributeName].inputType].allowedOperators.some((operator)=>operator===this.operator)) {
      errorCode = MembershipConditionsErrors.EqualityMismatchInputField.errorCode;
      message = MembershipConditionsErrors.EqualityMismatchInputField.message;
    }
    return membershipConditionErrorResponse(errorCode, this.key, message);
  }
  returnMembershipConditionErrorCode() {
    return returnMembershipConditionErrorCodeForAllowedSimpleOperators(this);
  }
  static createEqualityNode(attributeName, value) {
    const attributeType = InputFields[attributeName].attributeType;
    const operands = {
      attributeName,
      attributeType,
      attributeValueToBeMatched: value,
    };
    return new Equality({ operands }, null);
  }
}

export class StringIn extends SimpleCondition {
  operator = Operators.comparisonSTRING_IN;
  constructor(condition, parentNode) {
    super(condition.id, condition.pathFromRoot, parentNode);
    this.setIsKeyCondition();
    this.addOperands(condition[this.operandKey]);
    this.addLabel();
  }
  addLabel() {
    this.label = InputFields[this.getAttributeName()]?.attributeLabel;
  }
  getAttributeName() {
    return this.getOperands().attributeName;
  }
  setValue(value) {
    this[this.operandKey] = { ...this.getOperands(), attributeValuesToBeMatched: value };
  }
  getValue() {
    return this[this.operandKey].attributeValuesToBeMatched;
  }
  renderCond() {
    return (
      <SimpleConditionValueRender
        label={this.label}
        value={this[this.operandKey].attributeValuesToBeMatched}
      />
    );
  }
  renderCondAsString() {
    return (
      <SimpleConditionStringRender
        label={this.label}
        value={this[this.operandKey].attributeValuesToBeMatched}
      />
    );
  }

  returnErrorForConflictInputFieldsAndOperators() {
    let errorCode, message;
    if (!InputType[InputFields[this.getOperands().attributeName].inputType].allowedOperators.some((operator)=>operator===this.operator)) {
      errorCode = MembershipConditionsErrors.StringInMismatchInputField.errorCode;
      message = MembershipConditionsErrors.StringInMismatchInputField.message;
    }
    return membershipConditionErrorResponse(errorCode, this.key, message);
  }
  returnMembershipConditionErrorCode() {
    return returnMembershipConditionErrorCodeForAllowedSimpleOperators(this);
  }
  static createStringInNode(attributeName, value) {
    const attributeType = InputFields[attributeName].attributeType;
    const operands = {
      attributeName,
      attributeType,
      attributeValuesToBeMatched: value,
    };
    return new StringIn({ operands }, null);
  }
}

export class SpecialEquality extends SimpleCondition {
  operator = Operators.comparisonSPECIAL_EQ;
  constructor(condition, parentNode) {
    super(condition.id, condition.pathFromRoot, parentNode);
    this.setIsKeyCondition();
    this.addOperands(condition[this.operandKey]);
    this.reason = condition.reason;
  }

  returnMembershipConditionErrorCode() {
    let errorCode, message;
    if (this.parent?.operator === Operators.logicalAND) {
      errorCode = MembershipConditionsErrors.ConjunctionContainsSpecialCondition.errorCode;
      message = MembershipConditionsErrors.ConjunctionContainsSpecialCondition.message;
    }
    return membershipConditionErrorResponse(errorCode, this.key, message);
  }
  static createSpecialEqualityNode(reason, userId) {
    const operands = {
      uniqueAttribute: "id",
      attributeType: "user",
      attributeValueToBeMatched: userId,
    };
    return new SpecialEquality({ operands, reason }, null);
  }
}

export class NullCondition extends ConditionNode {
  operator = Operators.noConditionNull;
  constructor(condition, parentNode) {
    super(condition.id, condition.pathFromRoot, parentNode);
  }
  isSingleOperand() {
    return true;
  }
  returnMembershipConditionErrorCode() {
    let errorCode, message;
    if (heightOfNode(this) > 0) {
      errorCode = MembershipConditionsErrors.NullConditionAboveHeight0.errorCode;
      message = MembershipConditionsErrors.NullConditionAboveHeight0.message;
    }
    return membershipConditionErrorResponse(errorCode, this.key, message);
  }
  toJSON(requestFormat = false) {
    let jsonResponse = {
      operator: this.operator,
    };
    if (requestFormat === false) {
      jsonResponse = {
        ...jsonResponse,
        id: this.key,
        pathFromRoot: this.pathFromRoot,
        isKeyCondition: this.isKeyCondition,
      };
    }
    return jsonResponse;
  }
}
